import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Clock, MapPin, Users, BookOpen, HandHeart, CalendarDays, ArrowRight } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import { useVisitor } from "@/lib/visitor";
import { Link } from "react-router-dom";
import { IMG } from "@/lib/content";
import { useContent } from "@/lib/content-store";
import { describe } from "@/lib/schedule";
import { eventPhotoUrl } from "@/lib/api";
import { publicApi } from "@/lib/api";
import { AutoRail } from "@/components/AutoRail";

const Reveal = ({ children, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6 }} className={className}>{children}</motion.div>
);

const Eyebrow = ({ children }) => <p className="text-xs font-semibold uppercase tracking-wider text-[#1D64B4]">{children}</p>;
const H2 = ({ children }) => <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">{children}</h2>;

export const VisitSection = () => {
  const CHURCH = useContent();
  return (
  <section id="visit" className="bg-white py-16 lg:py-24" data-testid="visit-section">
    <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-2 lg:items-center lg:px-10">
      <Reveal>
        <Eyebrow>Plan a visit</Eyebrow>
        <H2>Worship with us at Victory Parañaque</H2>
        <p className="mt-4 max-w-lg text-slate-600">Come as you are. Expect heartfelt worship, a practical message from the Bible and people glad to meet you.</p>
        <div className="mt-8 space-y-3">
          {(useContent().schedules || []).map((s, i) => (
            <div key={i} className="flex items-center gap-4 rounded-2xl border border-slate-200 p-4" data-testid={`service-time-${i}`}>
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#EBF3FA] text-[#0A3A6B]"><Clock className="h-5 w-5" /></span>
              <div><p className="font-display font-semibold text-slate-900">{s.title}</p><p className="text-sm text-slate-500">{describe(s)}{s.note && ` · ${s.note}`}{s.venue && ` · ${s.venue}`}</p></div>
            </div>
          ))}
          <p className="flex items-center gap-2 pt-2 text-sm text-slate-500"><MapPin className="h-4 w-4" /> {CHURCH.address}</p>
        </div>
      </Reveal>
      <Reveal><img src={IMG.stage} alt="Sunday worship" className="h-[260px] w-full rounded-3xl object-cover sm:h-[420px]" /></Reveal>
    </div>
  </section>
  );
};

export const JesusSection = () => {
  const { openAction } = useVisitor();
  return (
    <section id="jesus" className="bg-[#F1F5F9] py-16 lg:py-24" data-testid="jesus-section">
      <Reveal className="mx-auto max-w-4xl px-5 lg:px-10">
        <Eyebrow>Know Jesus</Eyebrow>
        <p className="font-serif-accent mt-6 text-2xl leading-snug text-[#0A3A6B] sm:text-3xl lg:text-4xl">
          "For God so loved the world that he gave his one and only Son, that whoever believes in him shall not perish but have eternal life."
        </p>
        <p className="mt-4 text-sm font-semibold text-slate-500">John 3:16</p>
        <p className="mt-8 max-w-2xl text-slate-600">Questions about faith, life or God? You don't have to figure it out alone. A pastor or leader would love to talk and pray with you.</p>
        <Button onClick={() => openAction("talk_pastor")} className="mt-8 rounded-full bg-[#0A3A6B] px-7 hover:bg-[#1D64B4]" data-testid="jesus-talk-button">
          Talk with someone <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </Reveal>
    </section>
  );
};

const GrowCard = ({ icon: Icon, title, text, img, cta, onClick, testId }) => (
  <Reveal className="group overflow-hidden rounded-3xl border border-slate-200 bg-white">
    <img src={img} alt={title} className="h-56 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
    <div className="p-8">
      <Icon className="h-6 w-6 text-[#1D64B4]" />
      <h3 className="mt-4 text-xl font-semibold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
      <Button variant="ghost" onClick={onClick} className="mt-4 px-0 text-[#0A3A6B] hover:bg-transparent hover:text-[#1D64B4]" data-testid={testId}>
        {cta} <ArrowRight className="ml-1 h-4 w-4" />
      </Button>
    </div>
  </Reveal>
);

export const GroupsSection = () => {
  const { openAction } = useVisitor();
  return (
    <section id="groups" className="bg-white py-16 lg:py-24" data-testid="groups-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <Reveal><Eyebrow>Grow</Eyebrow><H2>Grow in faith, together</H2></Reveal>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <GrowCard icon={Users} title="Victory Groups" img={IMG.group} cta="Find a Victory Group" testId="groups-join-button"
            text="Small groups that meet regularly to read the Bible, pray and do life together." onClick={() => openAction("victory_group")} />
          <GrowCard icon={BookOpen} title="Discipleship Journey" img={IMG.bible} cta="Start the Journey" testId="journey-start-button"
            text="The Church's discipleship course — step-by-step training to grow as a follower of Jesus." onClick={() => openAction("journey")} />
        </div>
      </div>
    </section>
  );
};

export const ServeSection = () => {
  const { openAction } = useVisitor();
  const CHURCH = useContent();
  return (
    <section id="serve" className="bg-[#0A3A6B] py-16 text-white lg:py-24" data-testid="serve-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-[1fr_1.2fr] lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-wider text-sky-300">Serve</p>
          <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Find your place on the team</h2>
          <p className="mt-4 max-w-md text-sky-100/80">Every Sunday is made possible by volunteers using their gifts to serve God and people.</p>
          <Button onClick={() => openAction("serve")} className="mt-8 rounded-full bg-white px-7 text-[#0A3A6B] hover:bg-sky-100" data-testid="serve-join-button">
            <HandHeart className="mr-1 h-4 w-4" /> I want to serve
          </Button>
        </Reveal>
        <div className="min-w-0">
          <AutoRail section="serve" items={CHURCH.ministries} gap="gap-3" mobileCols="grid-cols-2 sm:grid-cols-3" slideBasis="basis-1/2 sm:basis-1/3" render={(m, i) => (
            <Reveal key={m}><div className="h-full rounded-2xl border border-white/10 bg-white/5 p-5 text-sm font-medium transition-colors hover:bg-white/10" data-testid={`ministry-${i}`}>{m}</div></Reveal>
          )} />
        </div>
      </div>
    </section>
  );
};

export const EventsSection = ({ limit = 6 }) => {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    publicApi.get("/public/events", { params: { limit } }).then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, [limit]);
  return (
    <section id="events" className="bg-[#F8FAFC] py-16 lg:py-24" data-testid="events-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <Reveal><Eyebrow>Announcements</Eyebrow><H2>What's coming up</H2></Reveal>
        {events.length === 0 ? (
          <p className="mt-8 text-slate-500" data-testid="events-empty">We're preparing something special. New gatherings will be shared here soon.</p>
        ) : (
          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {events.map((e) => (
              <Reveal key={e.id}>
                <Link to={`/events/${e.id}`} data-testid={`public-event-${e.id}`}
                  className="group block h-full overflow-hidden rounded-3xl border border-slate-200 bg-white transition-shadow hover:shadow-xl">
                  <img src={e.has_photo ? eventPhotoUrl(e.id) : IMG.praise} alt={e.title} className="h-44 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="p-6">
                    <p className="flex items-center gap-2 text-sm font-semibold text-[#1D64B4]"><CalendarDays className="h-4 w-4" />{format(parseISO(e.date), "EEE, MMM d")}{e.start_time && ` · ${e.start_time}`}</p>
                    <h3 className="mt-3 text-xl font-semibold text-slate-900">{e.title}</h3>
                    {e.location && <p className="mt-2 flex items-center gap-1.5 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" />{e.location}</p>}
                    <p className="mt-4 flex items-center gap-1 text-sm font-semibold text-[#0A3A6B] group-hover:text-[#1D64B4]">View event <ArrowRight className="h-4 w-4" /></p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
